import SEO from '../components/SEO';
import { Link } from 'react-router-dom';

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="text-xl font-bold text-gray-900 mb-3 font-heading">{title}</h2>
    <div className="text-gray-600 text-sm leading-relaxed space-y-3">{children}</div>
  </div>
);

export default function PrivacyPage() {
  return (
    <>
      <SEO
        title="Privacy Policy"
        description="Privacy Policy for MDS Transportation LLC — how we collect, use, and protect your personal information."
        path="/privacy"
      />

      {/* Page hero — sits directly under the shared navbar */}
      <div className="bg-purple-700 py-14 px-4">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h1 className="text-4xl font-bold font-heading mb-2">Privacy Policy</h1>
          <p className="text-purple-200 text-sm">Last updated: January 1, 2024</p>
        </div>
      </div>

      {/* Content */}
      <div className="bg-gray-50 py-14 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="card shadow-sm border border-gray-100">
            <p className="text-gray-600 text-sm leading-relaxed mb-8">
              MDS Transportation LLC ("we," "us," or "our") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our services or visit our website.
            </p>

            <Section title="1. Information We Collect">
              <p><strong>Personal Information:</strong> When you book a ride or create an account, we may collect:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Full name, email address, and phone number</li>
                <li>Pickup and destination addresses</li>
                <li>Mobility needs and special requirements</li>
                <li>Emergency contact information</li>
                <li>Payment information (processed securely — we do not store full card numbers)</li>
              </ul>
              <p><strong>Usage Data:</strong> We automatically collect certain information when you use our website, including IP address, browser type, pages visited, and time spent on pages.</p>
            </Section>

            <Section title="2. How We Use Your Information">
              <p>We use your information to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Process and confirm ride bookings</li>
                <li>Communicate with you about your rides (confirmation, driver assignment, reminders)</li>
                <li>Send receipts and invoices</li>
                <li>Improve our services and customer experience</li>
                <li>Comply with legal obligations</li>
                <li>Respond to your inquiries and support requests</li>
              </ul>
              <p>We do <strong>not</strong> sell or rent your personal information to third parties.</p>
            </Section>

            <Section title="3. Medical Information">
              <p>We collect limited medical-related information (such as wheelchair needs or special medical conditions) solely for the purpose of providing appropriate transportation. This information is treated with the highest level of confidentiality and is not shared with third parties except as required to provide your ride service.</p>
            </Section>

            <Section title="4. Information Sharing">
              <p>We may share your information with:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Drivers:</strong> Your name, pickup location, destination, and relevant mobility needs are shared with your assigned driver.</li>
                <li><strong>Service Providers:</strong> Third-party vendors who assist with payment processing, email delivery, and IT services — all bound by confidentiality agreements.</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or government authority.</li>
              </ul>
            </Section>

            <Section title="5. Data Security">
              <p>We implement industry-standard security measures to protect your personal information, including encrypted data transmission (SSL/TLS), secure password hashing, and access controls. However, no method of internet transmission is 100% secure, and we cannot guarantee absolute security.</p>
            </Section>

            <Section title="6. Data Retention">
              <p>We retain your personal information for as long as necessary to provide services and comply with our legal obligations. You may request deletion of your account and associated data by contacting us directly.</p>
            </Section>

            <Section title="7. Cookies">
              <p>Our website uses cookies to enhance your browsing experience and analyze site traffic. You can control cookie settings through your browser preferences. Disabling cookies may limit some website functionality.</p>
            </Section>

            <Section title="8. Your Rights">
              <p>You have the right to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your personal data</li>
                <li>Opt out of marketing communications at any time</li>
              </ul>
              <p>To exercise these rights, contact us using the information below.</p>
            </Section>

            <Section title="9. Children's Privacy">
              <p>Our services are not directed to children under 13. We do not knowingly collect personal information from children under 13. If you believe we have inadvertently collected such information, please contact us immediately.</p>
            </Section>

            <Section title="10. Contact Us">
              <p>For privacy-related questions or requests, please contact:</p>
              <div className="bg-gray-50 rounded-lg p-4 mt-2 text-sm">
                <p><strong>MDS Transportation LLC — Privacy Officer</strong></p>
                <p>5632 W Washington Street, Indianapolis, IN 46241</p>
                <p>📞 <a href="tel:+13179821959" className="text-purple-600">+1 (317) 982-1959</a></p>
                <p>📧 <a href="mailto:info@mdstransportation.com" className="text-purple-600">info@mdstransportation.com</a></p>
              </div>
            </Section>
          </div>

          <div className="text-center mt-8">
            <Link to="/terms" className="text-purple-600 hover:underline text-sm">
              View Terms of Service →
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
